import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getTodaySummary, updatePlannerStatus } from "./TodayService";
import "./Today.css";

const VIEW_KEY = "lifeos-view-mode";
const VIEW_OPTIONS = [
  { label: "Dark", value: "dark" },
  { label: "Light", value: "light" },
  { label: "Developer", value: "developer" },
];

export default function Today() {
  const nav = useNavigate();
  const [summary, setSummary] = useState({ urgent: [], reminders: [], payments: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rangeDays, setRangeDays] = useState(15);
  const [updatingId, setUpdatingId] = useState("");
  const [toast, setToast] = useState({ message: "", type: "" });
  const [viewMode, setViewMode] = useState("dark");

  useEffect(() => {
    const storedMode = localStorage.getItem(VIEW_KEY);
    if (storedMode && VIEW_OPTIONS.some((option) => option.value === storedMode)) {
      setViewMode(storedMode);
    }
  }, []);

  useEffect(() => {
    document.body.dataset.viewMode = viewMode;
    localStorage.setItem(VIEW_KEY, viewMode);
  }, [viewMode]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    getTodaySummary(rangeDays)
      .then((data) => {
        if (!alive) return;
        setSummary({
          urgent: data?.urgent || [],
          reminders: data?.reminders || [],
          payments: data?.payments || [],
        });
      })
      .catch(() => {
        if (!alive) return;
        setError("Unable to load today summary.");
      })
      .finally(() => {
        if (!alive) return;
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [rangeDays]);

  const formatDate = (d) => {
    if (!d) return "No date";
    return new Date(d).toLocaleDateString();
  };

  const splitByDate = (items, dateKey) => {
    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const passed = [];
    const upcoming = [];
    items.forEach((item) => {
      const raw = item[dateKey];
      if (!raw) {
        upcoming.push(item);
        return;
      }
      const date = new Date(raw);
      if (date < startOfToday) {
        passed.push(item);
      } else {
        upcoming.push(item);
      }
    });
    return { passed, upcoming };
  };

  const counts = useMemo(
    () => ({
      urgent: summary.urgent.length,
      reminders: summary.reminders.length,
      payments: summary.payments.length,
    }),
    [summary]
  );

  const handleStatus = async (id, status) => {
    setUpdatingId(id);
    try {
      await updatePlannerStatus(id, status);
      setSummary((prev) => ({
        urgent: prev.urgent.filter((i) => i.id !== id),
        reminders: prev.reminders.filter((i) => i.id !== id),
        payments: prev.payments.filter((i) => i.id !== id),
      }));
      setToast({ message: `Marked as ${status.toLowerCase()}.`, type: "success" });
    } catch {
      setError("Unable to update item status.");
      setToast({ message: "Update failed. Try again.", type: "error" });
    } finally {
      setUpdatingId("");
    }
  };

  useEffect(() => {
    if (!toast.message) return undefined;
    const timer = setTimeout(() => {
      setToast({ message: "", type: "" });
    }, 2500);
    return () => clearTimeout(timer);
  }, [toast.message]);

  const rangeOptions = [
    { label: "Today", value: 0 },
    { label: "7 days", value: 7 },
    { label: "15 days", value: 15 },
  ];


  return (
    <div className="today-container">
      {toast.message && <div className={`today-toast today-toast-${toast.type}`}>{toast.message}</div>}
      <div className="today-topbar">
        <h1 className="today-title">Today</h1>

        <div className="today-view-switch" role="radiogroup" aria-label="View mode">
          {VIEW_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={viewMode === option.value}
              className={`today-view-btn ${viewMode === option.value ? "active" : ""}`}
              onClick={() => setViewMode(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="today-range">
        {rangeOptions.map((opt) => (
          <button
            key={opt.value}
            className={`today-range-btn ${rangeDays === opt.value ? "active" : ""}`}
            onClick={() => setRangeDays(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="today-grid">
        <div className="today-card today-card-urgent">
          <h3 className="today-card-title">
            <span className="today-pill today-pill-urgent">Urgent {counts.urgent}</span>
          </h3>
          {loading ? (
            <p className="today-muted">Loading...</p>
          ) : error ? (
            <p className="today-muted">{error}</p>
          ) : summary.urgent.length === 0 ? (
            <div className="today-empty">
              <p>You are clear today.</p>
              <button className="today-ghost-btn" onClick={() => nav("/planner")}>
                Add a task
              </button>
            </div>
          ) : (
            <TodaySection
              data={splitByDate(summary.urgent, "dueDate")}
              renderItem={(item) => (
                <TodayItem
                  title={item.title}
                  meta={[item.type, formatDate(item.dueDate)]}
                  actions={
                    <ItemActions
                      disabled={updatingId === item.id}
                      onDone={() => handleStatus(item.id, "DONE")}
                      onSkip={() => handleStatus(item.id, "SKIPPED")}
                    />
                  }
                />
              )}
            />
          )}
        </div>

        <div className="today-card today-card-reminders">
          <h3 className="today-card-title">
            <span className="today-pill today-pill-reminders">Reminders {counts.reminders}</span>
          </h3>
          {loading ? (
            <p className="today-muted">Loading...</p>
          ) : error ? (
            <p className="today-muted">{error}</p>
          ) : summary.reminders.length === 0 ? (
            <div className="today-empty">
              <p>No reminders in this range.</p>
              <button className="today-ghost-btn" onClick={() => nav("/planner")}>
                Add a reminder
              </button>
            </div>
          ) : (
            <TodaySection
              data={splitByDate(summary.reminders, "dueDate")}
              renderItem={(item) => (
                <TodayItem
                  title={item.title}
                  meta={[item.type, formatDate(item.dueDate)]}
                  actions={
                    <ItemActions
                      disabled={updatingId === item.id}
                      onDone={() => handleStatus(item.id, "DONE")}
                      onSkip={() => handleStatus(item.id, "SKIPPED")}
                    />
                  }
                />
              )}
            />
          )}
        </div>

        <div className="today-card today-card-payments">
          <h3 className="today-card-title">
            <span className="today-pill today-pill-payments">Payments {counts.payments}</span>
          </h3>
          {loading ? (
            <p className="today-muted">Loading...</p>
          ) : error ? (
            <p className="today-muted">{error}</p>
          ) : summary.payments.length === 0 ? (
            <div className="today-empty">
              <p>No payments due.</p>
              <button className="today-ghost-btn" onClick={() => nav("/planner")}>
                Add a payment
              </button>
            </div>
          ) : (
            <TodaySection
              data={splitByDate(summary.payments, "paymentDate")}
              renderItem={(item) => (
                <TodayItem
                  title={item.title}
                  meta={[item.paymentType, formatDate(item.paymentDate), item.amount ?? "0"]}
                  actions={
                    <ItemActions
                      disabled={updatingId === item.id}
                      onDone={() => handleStatus(item.id, "DONE")}
                      onSkip={() => handleStatus(item.id, "SKIPPED")}
                    />
                  }
                />
              )}
            />
          )}
        </div>

        <div className="today-card today-card-full">
          <h3>⚡ Quick Actions</h3>

          <div className="today-actions">
            <Action label="Planner" emoji="🗓" onClick={() => nav("/planner")} />
            <Action label="Next Day" emoji="🧭" onClick={() => nav("/planner/next-day")} />
            <Action label="Finance" emoji="💰" onClick={() => nav("/finance")} />
            <Action label="Calendar" emoji="📅" onClick={() => nav("/calendar")} />
            <Action label="Journal" emoji="📓" onClick={() => nav("/journal")} />
            <Action label="Notes" emoji="📝" onClick={() => nav("/notes")} />
            <Action label="Files" emoji="📁" onClick={() => nav("/files")} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Action({ label, emoji, onClick }) {
  return (
    <div onClick={onClick} className="today-action-card">
      <div className="today-action-emoji">{emoji}</div>
      <div className="today-action-label">{label}</div>
    </div>
  );
}

function TodaySection({ data, renderItem }) {
  return (
    <div className="today-list today-list-scroll">
      {data.passed.length > 0 && (
        <>
          <div className="today-section-title">Date passed</div>
          <div className="today-chip-grid">
            {data.passed.map((item) => (
              <div key={item.id} className="today-chip">
                {renderItem(item)}
              </div>
            ))}
          </div>
        </>
      )}
      {data.upcoming.length > 0 && (
        <>
          <div className="today-section-title">Upcoming</div>
          <div className="today-chip-grid">
            {data.upcoming.map((item) => (
              <div key={item.id} className="today-chip">
                {renderItem(item)}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function TodayItem({ title, meta, actions }) {
  return (
    <>
      <div className="today-chip-title">{title}</div>
      <div className="today-chip-meta">
        {meta.filter(Boolean).map((m, idx) => (
          <span key={idx} className="today-chip-tag">
            {m}
          </span>
        ))}
      </div>
      {actions}
    </>
  );
}

function ItemActions({ onDone, onSkip, disabled }) {
  return (
    <div className="today-chip-actions">
      <button className="today-chip-btn today-chip-btn-done" onClick={onDone} disabled={disabled}>
        Done
      </button>
      <button className="today-chip-btn today-chip-btn-skip" onClick={onSkip} disabled={disabled}>
        Skip
      </button>
    </div>
  );
}
